// Die Seiten des Einstellungsbereichs: EINE Liste für die linke Navigation, die Übersicht (/settings) und die
// Befehlspalette (design-system §3.3). Jeder Eintrag nennt das Recht, ohne das er nicht erscheint – sichtbar ist nur,
// was die Person verwalten darf. Ohne Bausteine und ohne CSS: Das Manifest (web/index.ts) lädt diese Datei in Node.
import type { IconComponent } from '@web/module'
import {
  Activity,
  Bot,
  Building2,
  FileSearch,
  Hash,
  KeyRound,
  ListPlus,
  LockKeyhole,
  Mail,
  MailPlus,
  Plug,
  ShieldCheck,
  SlidersHorizontal,
  ToggleRight,
  UserCog,
  UserRound,
  Users,
} from 'lucide-react'

export interface SettingsEntry {
  /** Kennung der Seite (Teil der Befehls-ID) */
  id: string
  to: string
  label: string
  /** ein Satz für die Übersicht: was man hier tut */
  description: string
  icon: IconComponent
  permission?: string
  keywords?: readonly string[]
}

export interface SettingsGroup {
  key: 'company' | 'access' | 'data' | 'system' | 'personal'
  label: string
  entries: readonly SettingsEntry[]
}

export const SETTINGS_GROUPS: readonly SettingsGroup[] = [
  {
    key: 'company',
    label: 'Unternehmen',
    entries: [
      {
        id: 'company',
        to: '/settings/company',
        label: 'Unternehmen',
        description: 'Firmendaten für Belege, Logo und Hausfarbe, Belegtexte, Geschäftsjahr.',
        icon: Building2,
        permission: 'platform.settings.read',
        keywords: ['Firma', 'Anschrift', 'Logo', 'Hausfarbe', 'Bankverbindung', 'Belegtext', 'Impressum'],
      },
      {
        id: 'features',
        to: '/settings/features',
        label: 'Funktionen',
        description: 'Branchenprofil und Funktionsschalter: was Ihr Betrieb braucht – und was er nicht sehen muss.',
        icon: ToggleRight,
        permission: 'platform.settings.read',
        keywords: ['Branchenprofil', 'Schalter', 'Pfand', 'Chargen', 'MHD', 'Kasse', 'Module'],
      },
      {
        id: 'numbers',
        to: '/settings/numbers',
        label: 'Nummernkreise',
        description: 'Muster und nächste Nummer je Belegart.',
        icon: Hash,
        permission: 'platform.settings.read',
        keywords: ['Rechnungsnummer', 'Belegnummer', 'Muster', 'Zähler'],
      },
      {
        id: 'customFields',
        to: '/settings/custom-fields',
        label: 'Zusatzfelder',
        description: 'Eigene Felder für Artikel, Kunden, Lieferanten, Belege und Standorte.',
        icon: ListPlus,
        permission: 'platform.settings.read',
        keywords: ['freie Felder', 'eigenes Feld', 'Merkmal'],
      },
    ],
  },
  {
    key: 'access',
    label: 'Zugriff',
    entries: [
      {
        id: 'people',
        to: '/settings/people',
        label: 'Personen',
        description: 'Wer zum Betrieb gehört: einladen, sperren, Rollen je Standort zuweisen.',
        icon: Users,
        permission: 'platform.user.read',
        keywords: ['Benutzer', 'Mitarbeiter', 'einladen', 'Zugang', 'sperren', 'Kennwort-Link', 'Sitzungen beenden'],
      },
      {
        id: 'security',
        to: '/settings/security',
        label: 'Sicherheit',
        description: 'Zwei-Faktor-Pflicht, Übergangsfrist, vertraute Geräte – und das Anmeldeprotokoll der Personen.',
        icon: LockKeyhole,
        permission: 'platform.user.read',
        keywords: ['Zwei-Faktor', '2FA', 'Anmeldung', 'Code', 'Authenticator', 'Anmeldeprotokoll', 'Fehlversuche'],
      },
      {
        id: 'roles',
        to: '/settings/roles',
        label: 'Rollen & Rechte',
        description: 'Die Matrix Recht × Rolle mit Grenzen wie höchstem Rabatt oder Korrekturwert.',
        icon: ShieldCheck,
        permission: 'platform.role.read',
        keywords: ['Rechte', 'Berechtigung', 'Matrix', 'Grenze', 'Rabatt'],
      },
      {
        id: 'rights',
        to: '/settings/rights',
        label: 'Was darf diese Person?',
        description: 'Die wirksamen Rechte einer Person an einem Standort – in Worten, mit Grenzen.',
        icon: UserCog,
        permission: 'platform.role.read',
        keywords: ['wirksame Rechte', 'darf', 'Auskunft', 'Person'],
      },
      {
        id: 'assistant',
        to: '/settings/assistant',
        label: 'KI-Assistent',
        description: 'Ein- und ausschalten, Datenschutzhinweis, Aufbewahrung, Monatsbudget, erweiterte Aktionen.',
        icon: Bot,
        permission: 'assistant.settings.manage',
        keywords: ['KI', 'Assistent', 'Budget', 'Datenschutz', 'Aufbewahrung'],
      },
      {
        id: 'audit',
        to: '/settings/audit',
        label: 'Audit-Protokoll',
        description: 'Wer hat wann was über welchen Weg geändert – filtern, exportieren, Siegel prüfen.',
        icon: FileSearch,
        permission: 'platform.audit.read',
        keywords: ['Protokoll', 'Historie', 'wer hat', 'Export', 'Siegel'],
      },
      {
        id: 'privacy',
        to: '/settings/privacy',
        label: 'Datenschutz',
        description: 'Auskunft über gespeicherte Daten einer Person und Löschung nach Fristablauf.',
        icon: KeyRound,
        permission: 'platform.privacy.manage',
        keywords: ['DSGVO', 'Auskunft', 'Löschung', 'anonymisieren'],
      },
    ],
  },
  {
    key: 'data',
    label: 'Daten & Anbindung',
    entries: [
      {
        id: 'integrations',
        to: '/settings/integrations',
        label: 'Anbindungen',
        description: 'API-Schlüssel technischer Zugänge, Webhooks mit Zustellprotokoll, Importläufe.',
        icon: Plug,
        permission: 'platform.integration.read',
        keywords: ['API', 'Schlüssel', 'Token', 'Webhook', 'Schnittstelle', 'Bestell-App'],
      },
      {
        id: 'mail',
        to: '/settings/mail',
        label: 'E-Mail',
        description: 'Postausgang mit Zustellung, Absender, Vorlagen, Antwort-An und Sperrliste.',
        icon: Mail,
        permission: 'platform.mail.read',
        keywords: ['E-Mail', 'Mail', 'Postausgang', 'Absender', 'Vorlage', 'Sperrliste', 'Mailserver', 'Testmail'],
      },
      {
        id: 'mailInbox',
        to: '/settings/mail-inbox',
        label: 'E-Mail-Eingang',
        description: 'Postfach für eingehende E-Mails (IMAP), Verbindung testen, E-Mail-Dateien einlesen.',
        icon: MailPlus,
        permission: 'platform.mail.inbox',
        keywords: ['E-Mail', 'Posteingang', 'IMAP', 'Postfach', 'Anfragen', 'Bestellungen per E-Mail', '.eml'],
      },
    ],
  },
  {
    key: 'system',
    label: 'System',
    entries: [
      {
        id: 'system',
        to: '/settings/system',
        label: 'Systemzustand',
        description: 'Hintergrundjobs, nächtliche Bestandsprüfung, Suchindex, Speicher, Versionen.',
        icon: Activity,
        permission: 'platform.system.manage',
        keywords: ['Jobs', 'Suchindex', 'Speicher', 'Version', 'Migration'],
      },
    ],
  },
  {
    key: 'personal',
    label: 'Persönlich',
    entries: [
      {
        id: 'profile',
        to: '/settings/profile',
        label: 'Mein Profil',
        description: 'Name, Kennwort, Zwei-Faktor-Anmeldung, angemeldete Geräte, E-Mail-Benachrichtigungen, PIN, Darstellung.',
        icon: UserRound,
        keywords: ['Profil', 'Kennwort', 'Zwei-Faktor', 'Geräte', 'PIN', 'Darstellung', 'dunkel', 'Dichte', 'abmelden'],
      },
    ],
  },
]

/** Einstellungsseiten anderer Module (ihre Einträge der Gruppe „Einstellungen“) stehen in diesen Gruppen. */
export const FOREIGN_GROUP: Record<string, SettingsGroup['key']> = { '/settings/import': 'data' }
export const FOREIGN_ICON: IconComponent = SlidersHorizontal

export const ALL_SETTINGS_ENTRIES: readonly SettingsEntry[] = SETTINGS_GROUPS.flatMap((group) => group.entries)
